import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ArrowBackOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';
import { Box, IconButton, Paper } from '@mui/material';

function useModelIds() {
  const { state } = useLocation();
  return useMemo(() => {
    return !state || !state.modelIds ? null : (state.modelIds as string[]);
  }, [state]);
}

function usePreviousId(currentModelId: string, modelIds: string[] | null) {
  return useMemo(() => {
    if (modelIds === null) {
      return null;
    }
    const index = modelIds.indexOf(currentModelId);
    if (index <= 0) {
      return null;
    }
    return modelIds[index - 1];
  }, [modelIds, currentModelId]);
}

function useNextId(currentModelId: string, modelIds: string[] | null) {
  return useMemo(() => {
    if (modelIds === null) {
      return null;
    }
    const index = modelIds.indexOf(currentModelId);
    if (index >= modelIds.length - 1) {
      return null;
    }
    return modelIds[index + 1];
  }, [modelIds, currentModelId]);
}

type Props = {
  currentModelId: string;
  backLinkHref: string;
  getLinkHref: (id: string) => string;
};
export default function ListModelNavigation({ currentModelId, backLinkHref, getLinkHref }: Props): JsxNode {
  const modelIds = useModelIds();
  const navigate = useNavigate();
  const onClickBack = () => {
    if (history.length > 1) {
      history.back();
      return;
    }
    navigate(backLinkHref);
  };

  const renderArrows = () => {
    if (modelIds === null) {
      return null;
    }
    return <Arrows modelIds={modelIds} currentModelId={currentModelId} getLinkHref={getLinkHref} />;
  };

  return (
    <Box
      sx={{
        pt: { xs: 0, lg: 2 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: { xs: 'flex-start', lg: 'center' },
        position: { xs: 'normal', lg: 'sticky' },
        top: (theme) => theme.spacing(8),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'row', lg: 'column' } }}>
        <Paper sx={{ mb: { xs: 0, lg: 2 }, mr: { xs: 2, lg: 0 }, maxWidth: 48, borderRadius: 48 }}>
          <IconButton component="button" type="button" onClick={onClickBack} sx={{ p: 1.5 }}>
            <ArrowBackOutlined />
          </IconButton>
        </Paper>
        {renderArrows()}
      </Box>
    </Box>
  );
}

type ArrowsProps = {
  currentModelId: string;
  modelIds: string[];
  getLinkHref: (id: string) => string;
};
function Arrows({ currentModelId, modelIds, getLinkHref }: ArrowsProps) {
  const navigate = useNavigate();
  const previousId = usePreviousId(currentModelId, modelIds);
  const nextId = useNextId(currentModelId, modelIds);
  return (
    <Paper
      sx={{
        maxHeight: { xs: 48, lg: 'none' },
        maxWidth: { xs: 'none', lg: 48 },
        borderRadius: 48,
        p: 0.5,
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        flexDirection: { xs: 'row', lg: 'column' },
        mt: 0,
      }}
    >
      <IconButton
        size="large"
        sx={{ mb: { xs: 0, lg: 1 }, mr: { xs: 1, lg: 0 } }}
        disabled={previousId === null}
        onClick={() => {
          if (previousId) {
            navigate(getLinkHref(previousId), { state: { modelIds }, replace: true });
          }
        }}
      >
        <KeyboardArrowUpOutlined />
      </IconButton>
      <IconButton
        size="large"
        disabled={nextId === null}
        onClick={() => {
          if (nextId) {
            navigate(getLinkHref(nextId), { state: { modelIds }, replace: true });
          }
        }}
      >
        <KeyboardArrowDownOutlined />
      </IconButton>
    </Paper>
  );
}
